/* ─────────────────────────────────────────────
   Featured Collection Tabs — KaryaGold
   - Tab switcher with arrow-key support
   - Scroll progress bar (per-panel)
   - Swatch hover → variant image swap
───────────────────────────────────────────── */

if (!customElements.get('featured-collection-tabs')) {
  class FeaturedCollectionTabs extends HTMLElement {
    constructor() {
      super();
      this._onTabClick = this._onTabClick.bind(this);
      this._onTabKeydown = this._onTabKeydown.bind(this);
      this._onTrackScroll = this._onTrackScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._scrollRafId = null;
      this._resizeRafId = null;
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('.fct__tab'));
      this.panels = Array.from(this.querySelectorAll('.fct__panel'));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this._onTabClick);
        tab.addEventListener('keydown', this._onTabKeydown);
      });

      this.panels.forEach((panel) => {
        const track = panel.querySelector('[data-fct-track]');
        if (track) {
          track.addEventListener('scroll', this._onTrackScroll, { passive: true });
        }

        const prevBtn = panel.querySelector('[data-fct-prev]');
        const nextBtn = panel.querySelector('[data-fct-next]');
        if (prevBtn) prevBtn.addEventListener('click', () => this._scrollByPage(panel, -1));
        if (nextBtn) nextBtn.addEventListener('click', () => this._scrollByPage(panel, 1));

        this._setupSwatchInteractions(panel);
        this._updateProgress(panel);
        this._updateNavButtons(panel);
      });

      window.addEventListener('resize', this._onResize, { passive: true });
    }

    disconnectedCallback() {
      if (this.tabs) {
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this._onTabClick);
          tab.removeEventListener('keydown', this._onTabKeydown);
        });
      }
      if (this.panels) {
        this.panels.forEach((panel) => {
          const track = panel.querySelector('[data-fct-track]');
          if (track) track.removeEventListener('scroll', this._onTrackScroll);
        });
      }
      window.removeEventListener('resize', this._onResize);
      if (this._scrollRafId) cancelAnimationFrame(this._scrollRafId);
      if (this._resizeRafId) cancelAnimationFrame(this._resizeRafId);
    }

    /* ── Tab switching ─────────────────────── */

    _onTabClick(event) {
      this._activate(event.currentTarget);
    }

    _onTabKeydown(event) {
      const key = event.key;
      if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') {
        return;
      }
      event.preventDefault();
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      let nextIndex = currentIndex;
      if (key === 'ArrowLeft') nextIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
      else if (key === 'ArrowRight') nextIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
      else if (key === 'Home') nextIndex = 0;
      else if (key === 'End') nextIndex = this.tabs.length - 1;

      const nextTab = this.tabs[nextIndex];
      if (!nextTab) return;
      this._activate(nextTab);
      nextTab.focus();
    }

    _activate(activeTab) {
      const targetPanelId = activeTab.getAttribute('data-panel-id');
      if (!targetPanelId) return;

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel) => {
        const isActive = panel.id === targetPanelId;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
          const track = panel.querySelector('[data-fct-track]');
          if (track) {
            track.scrollTo({ left: 0, behavior: 'auto' });
          }
          requestAnimationFrame(() => {
            this._updateProgress(panel);
            this._updateNavButtons(panel);
          });
        } else {
          panel.setAttribute('hidden', 'hidden');
        }
      });
    }

    /* ── Progress bar ──────────────────────── */

    _onTrackScroll(event) {
      const track = event.currentTarget;
      const panel = track.closest('.fct__panel');
      if (!panel) return;
      if (this._scrollRafId) cancelAnimationFrame(this._scrollRafId);
      this._scrollRafId = requestAnimationFrame(() => {
        this._updateProgress(panel);
        this._updateNavButtons(panel);
      });
    }

    _onResize() {
      if (this._resizeRafId) cancelAnimationFrame(this._resizeRafId);
      this._resizeRafId = requestAnimationFrame(() => {
        this.panels.forEach((panel) => {
          this._updateProgress(panel);
          this._updateNavButtons(panel);
        });
      });
    }

    _updateProgress(panel) {
      const track = panel.querySelector('[data-fct-track]');
      const bar = panel.querySelector('[data-fct-progress-bar]');
      const progressEl = panel.querySelector('.fct__progress');
      if (!track || !bar || !progressEl) return;

      const scrollWidth = track.scrollWidth;
      const clientWidth = track.clientWidth;

      if (scrollWidth <= clientWidth + 1) {
        progressEl.classList.add('is-hidden');
        return;
      }
      progressEl.classList.remove('is-hidden');

      const trackEl = bar.parentElement;
      const trackElWidth = trackEl.clientWidth;
      const ratio = clientWidth / scrollWidth;
      const barWidthPx = Math.max(trackElWidth * ratio, 24);
      const maxBarLeft = trackElWidth - barWidthPx;
      const scrollProgress = track.scrollLeft / (scrollWidth - clientWidth);
      const barLeft = Math.min(Math.max(scrollProgress * maxBarLeft, 0), maxBarLeft);

      bar.style.width = barWidthPx + 'px';
      bar.style.transform = 'translateX(' + barLeft + 'px)';
    }

    /* ── Desktop navigation buttons ───────── */

    _scrollByPage(panel, direction) {
      const track = panel.querySelector('[data-fct-track]');
      if (!track) return;
      const slide = track.querySelector('.fct__slide');
      const gap = parseFloat(getComputedStyle(track).gap) || 20;
      const amount = slide ? slide.offsetWidth + gap : track.clientWidth * 0.8;
      track.scrollBy({ left: direction * amount * 2, behavior: 'smooth' });
    }

    _updateNavButtons(panel) {
      const track = panel.querySelector('[data-fct-track]');
      const prevBtn = panel.querySelector('[data-fct-prev]');
      const nextBtn = panel.querySelector('[data-fct-next]');
      if (!track) return;
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 1;
      if (nextBtn) nextBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    }

    /* ── Swatch hover → variant image ──────── */

    _setupSwatchInteractions(panel) {
      const cards = panel.querySelectorAll('[data-fct-card]');
      cards.forEach((card) => {
        const primaryImg = card.querySelector('[data-fct-card-primary]');
        if (!primaryImg) return;

        const originalSrc = primaryImg.getAttribute('data-original-src') || primaryImg.getAttribute('src');
        const originalSrcset =
          primaryImg.getAttribute('data-original-srcset') || primaryImg.getAttribute('srcset');

        const swatches = card.querySelectorAll('.fct-card__swatch');
        swatches.forEach((swatch) => {
          const variantSrc = swatch.getAttribute('data-variant-src');
          const variantSrcset = swatch.getAttribute('data-variant-srcset');
          if (!variantSrc) return;

          const onEnter = () => {
            primaryImg.setAttribute('src', variantSrc);
            if (variantSrcset) primaryImg.setAttribute('srcset', variantSrcset);
            card.classList.add('is-swatch-hover');
          };
          const onLeave = () => {
            if (originalSrc) primaryImg.setAttribute('src', originalSrc);
            if (originalSrcset) primaryImg.setAttribute('srcset', originalSrcset);
            card.classList.remove('is-swatch-hover');
          };

          swatch.addEventListener('mouseenter', onEnter);
          swatch.addEventListener('mouseleave', onLeave);
          swatch.addEventListener('focus', onEnter);
          swatch.addEventListener('blur', onLeave);
          swatch.addEventListener('touchstart', onEnter, { passive: true });
          swatch.addEventListener('touchend', onLeave, { passive: true });
        });
      });
    }
  }

  customElements.define('featured-collection-tabs', FeaturedCollectionTabs);
}
